import Phaser from 'phaser';

/**
 * Old CRT terminal post-process effect.
 *
 * Applied to GameScene.cameras.main only (HUD stays clean for readability).
 *
 * Knobs are public fields — tweak live via the browser console:
 *   window.__crt.scanlineIntensity = 0.5
 *   window.__crt.curvature = 0.12
 * Then call `window.__crtRefresh()` if needed.
 */
const CRT_FRAG = `
precision mediump float;

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform float uTime;
uniform float uScanlineIntensity;
uniform float uVignetteIntensity;
uniform float uChromaticOffset;
uniform float uCurvature;
uniform float uNoiseAmount;
uniform float uScanlineDensity;

varying vec2 outTexCoord;

vec2 curveUV(vec2 uv) {
  vec2 cuv = uv * 2.0 - 1.0;
  vec2 offset = abs(cuv.yx) / vec2(6.0);
  cuv = cuv + cuv * offset * offset * uCurvature * 5.0;
  return (cuv + 1.0) * 0.5;
}

float rand(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec2 uv = curveUV(outTexCoord);

  // Black bars where curvature pushed sample outside the screen
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  // Chromatic aberration — sample R/G/B at slightly offset UVs
  vec2 px = vec2(1.0) / uResolution;
  float ca = uChromaticOffset * px.x;
  vec3 col;
  col.r = texture2D(uMainSampler, uv + vec2(ca, 0.0)).r;
  col.g = texture2D(uMainSampler, uv).g;
  col.b = texture2D(uMainSampler, uv - vec2(ca, 0.0)).b;

  // Scanlines — modulated brightness per row
  float scanline = sin(uv.y * uResolution.y * uScanlineDensity) * 0.5 + 0.5;
  col *= mix(1.0, scanline, uScanlineIntensity);

  // Vignette — darker towards edges
  vec2 vUv = uv * (1.0 - uv.yx);
  float vig = vUv.x * vUv.y * 15.0;
  vig = pow(vig, 0.25);
  col *= mix(1.0, vig, uVignetteIntensity);

  // Static noise — drifts over time
  float n = (rand(uv + uTime * 0.0003) - 0.5) * uNoiseAmount;
  col += n;

  gl_FragColor = vec4(col, 1.0);
}
`;

export class CRTPipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  // Live knobs — start subtle, dial up if it reads well
  scanlineIntensity = 0.25;
  scanlineDensity   = 1.5;
  vignetteIntensity = 0.55;
  chromaticOffset   = 1.2;
  curvature         = 0.08;
  noiseAmount       = 0.04;

  constructor(game: Phaser.Game) {
    super({ game, name: 'CRT', fragShader: CRT_FRAG });
  }

  onPreRender(): void {
    this.set1f('uTime', this.game.loop.time);
    this.set1f('uScanlineIntensity', this.scanlineIntensity);
    this.set1f('uScanlineDensity', this.scanlineDensity);
    this.set1f('uVignetteIntensity', this.vignetteIntensity);
    this.set1f('uChromaticOffset', this.chromaticOffset);
    this.set1f('uCurvature', this.curvature);
    this.set1f('uNoiseAmount', this.noiseAmount);
  }

  onDraw(renderTarget: Phaser.Renderer.WebGL.RenderTarget): void {
    this.set2f('uResolution', renderTarget.width, renderTarget.height);
    this.bindAndDraw(renderTarget);
  }
}
